// TODO: SMT-1374 — extract shared DraggableRowBase to reduce duplication with RowEditor
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import dayjs from 'dayjs'
import { useDrag, useDrop, DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import SydModal from '../../../../commonDesign/SydModal'
import SydModalActions from '../../../../commonDesign/SydModal/SydModalActions'
import SydButton from '../../../../commonDesign/Button'
import SydSelect from '../../../../commonDesign/SydSelect'
import SydInput from '../../../../commonDesign/SydInput'
import SydLabel from '../../../../commonDesign/SydLabel'
import Switch from '../../../../molecules/Switch'
import Icon from '../../../../atoms/Icon'
import { ICON_NAMES } from '../../../../../constants'
import { QUICK_DATE_OPTIONS } from './constants'
import {
  ALL_ACTIVITY_TYPE_VALUES,
  CheckboxPopover,
  RowTypeDetail
} from './rowComponents'

const DRAG_TYPE = 'SETTINGS_ROW'

const sectionHeading = {
  fontSize: 'var(--font-size-xs)',
  fontWeight: 700,
  textTransform: 'uppercase',
  color: 'var(--text-color-subtle)',
  borderBottom: '1px solid var(--color-neutral-200)',
  paddingBottom: '4px',
  marginBottom: '8px'
}

function SettingsRow ({ row, index, activityRows, onToggle, onRowFieldChange, onMoveItem }) {
  const ref = useRef(null)
  const [popoverAnchor, setPopoverAnchor] = useState(null)
  const enabled = row.enabled !== false

  const [{ handlerId }, drop] = useDrop({
    accept: DRAG_TYPE,
    collect (monitor) { return { handlerId: monitor.getHandlerId() } },
    hover (item, monitor) {
      if (!ref.current) return
      const dragIndex = item.index
      const hoverIndex = index
      if (dragIndex === hoverIndex) return

      const rect = ref.current.getBoundingClientRect()
      const hoverMiddleY = (rect.bottom - rect.top) / 2
      const clientOffset = monitor.getClientOffset()
      if (!clientOffset) return
      const hoverClientY = clientOffset.y - rect.top

      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return

      onMoveItem(dragIndex, hoverIndex)
      item.index = hoverIndex
    }
  })

  const [{ isDragging }, drag] = useDrag({
    type: DRAG_TYPE,
    item: () => ({ id: row.key, index }),
    collect: (monitor) => ({ isDragging: monitor.isDragging() })
  })

  drag(drop(ref))

  const handleChipAreaClick = useCallback((e) => {
    e.stopPropagation()
    setPopoverAnchor(e.currentTarget)
  }, [])

  const handlePopoverClose = useCallback(() => {
    setPopoverAnchor(null)
  }, [])

  const activityTypeItems = useMemo(
    () => ALL_ACTIVITY_TYPE_VALUES.map(t => ({ key: t, label: t })),
    []
  )
  const activityTypeSelected = useMemo(
    () => new Set(row.activityTypes || []),
    [row.activityTypes]
  )
  const handleActivityTypeToggle = useCallback((type) => {
    const current = new Set(row.activityTypes || [])
    if (current.has(type)) { current.delete(type) } else { current.add(type) }
    onRowFieldChange(index, 'activityTypes', [...current])
  }, [index, row.activityTypes, onRowFieldChange])

  const mvChangeItems = useMemo(
    () => (activityRows || []).map(r => ({ key: r.key, label: r.name })),
    [activityRows]
  )
  const mvChangeSelected = useMemo(() => {
    if (!Array.isArray(row.includedActivityKeys)) {
      return new Set((activityRows || []).map(r => r.key))
    }
    return new Set(row.includedActivityKeys)
  }, [row.includedActivityKeys, activityRows])

  const handleMvChangeToggle = useCallback((key) => {
    const allKeys = (activityRows || []).map(r => r.key)
    const wasAll = !Array.isArray(row.includedActivityKeys)
    const current = wasAll ? new Set(allKeys) : new Set(row.includedActivityKeys)

    if (current.has(key)) { current.delete(key) } else { current.add(key) }

    const newKeys = [...current]
    const isAll = newKeys.length === allKeys.length &&
      allKeys.every(k => current.has(k))
    onRowFieldChange(index, 'includedActivityKeys', isAll ? null : newKeys)
  }, [index, row.includedActivityKeys, activityRows, onRowFieldChange])

  const showActivityPopover = row.rowType === 'activity'
  const showMvChangePopover = row.rowType === 'derived' && row.derivation === 'mvChange'

  return (
    <div
      ref={ref}
      data-handler-id={handlerId}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '8px',
        padding: '6px 4px',
        borderBottom: '1px solid var(--color-neutral-100)',
        opacity: isDragging ? 0.4 : enabled ? 1 : 0.5,
        transition: 'opacity 0.15s'
      }}
    >
      <div style={{ cursor: 'grab', display: 'flex', alignItems: 'center', flexShrink: 0, paddingTop: '2px' }}>
        <Icon name={ICON_NAMES.dragIndicator} customSize='1.2rem' />
      </div>
      <div style={{ flexShrink: 0, paddingTop: '1px' }}>
        <Switch
          checked={enabled}
          onChange={() => onToggle(index)}
          size='small'
        />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 'var(--font-size-sm)', fontWeight: 500 }}>{row.name}</div>
        <RowTypeDetail
          row={row}
          activityRows={activityRows}
          onChipAreaClick={handleChipAreaClick}
        />
      </div>
      {showActivityPopover && (
        <CheckboxPopover
          anchorEl={popoverAnchor}
          open={!!popoverAnchor}
          onClose={handlePopoverClose}
          heading='Activity Types'
          items={activityTypeItems}
          selectedKeys={activityTypeSelected}
          onToggle={handleActivityTypeToggle}
          minSelected={1}
        />
      )}
      {showMvChangePopover && (
        <CheckboxPopover
          anchorEl={popoverAnchor}
          open={!!popoverAnchor}
          onClose={handlePopoverClose}
          heading='Include in Formula'
          items={mvChangeItems}
          selectedKeys={mvChangeSelected}
          onToggle={handleMvChangeToggle}
        />
      )}
    </div>
  )
}

SettingsRow.propTypes = {
  row: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  activityRows: PropTypes.array.isRequired,
  onToggle: PropTypes.func.isRequired,
  onRowFieldChange: PropTypes.func.isRequired,
  onMoveItem: PropTypes.func.isRequired
}

export default function ActivitySummarySettingsDialog ({
  open,
  onClose,
  config,
  quickDate,
  customDateRange,
  rows,
  onApply,
  onRestoreDefaults
}) {
  const [localQuickDate, setLocalQuickDate] = useState(quickDate)
  const [localRange, setLocalRange] = useState(customDateRange)
  const [localRows, setLocalRows] = useState(rows)

  useEffect(() => {
    if (open) {
      setLocalQuickDate(quickDate)
      setLocalRange(customDateRange)
      setLocalRows(rows)
    }
  }, [open, quickDate, customDateRange, rows])

  const activityRows = useMemo(
    () => (localRows || []).filter(r => r.rowType === 'activity'),
    [localRows]
  )

  const dateOptions = useMemo(() => {
    const base = config.allowCustomDates !== false
      ? QUICK_DATE_OPTIONS
      : QUICK_DATE_OPTIONS.filter(o => o.value !== 'Custom')

    const available = config.availableQuickDates
    if (!available) return base
    const allowed = new Set(available)
    if (config.allowCustomDates !== false) allowed.add('Custom')
    return base.filter(o => allowed.has(o.value))
  }, [config.availableQuickDates, config.allowCustomDates])

  const handleDateSelect = useCallback((e) => {
    const val = e.target.value
    setLocalQuickDate(val)
    if (val !== 'Custom') setLocalRange(null)
  }, [])

  const handleStartDate = useCallback((e) => {
    setLocalRange(prev => ({
      startDate: e.target.value,
      endDate: prev?.endDate ?? dayjs().format('YYYY-MM-DD')
    }))
  }, [])

  const handleEndDate = useCallback((e) => {
    setLocalRange(prev => ({
      startDate: prev?.startDate ?? dayjs().startOf('year').format('YYYY-MM-DD'),
      endDate: e.target.value
    }))
  }, [])

  const handleToggle = useCallback((index) => {
    setLocalRows(prev => {
      const next = [...prev]
      next[index] = { ...next[index], enabled: next[index].enabled === false }
      return next
    })
  }, [])

  const handleRowFieldChange = useCallback((index, field, value) => {
    setLocalRows(prev => {
      const next = [...prev]
      next[index] = { ...next[index], [field]: value }
      return next
    })
  }, [])

  const handleMoveItem = useCallback((dragIndex, hoverIndex) => {
    setLocalRows(prev => {
      const next = [...prev]
      const [moved] = next.splice(dragIndex, 1)
      next.splice(hoverIndex, 0, moved)
      return next
    })
  }, [])

  const handleApply = useCallback(() => {
    onApply({
      quickDate: localQuickDate,
      customDateRange: localQuickDate === 'Custom' ? localRange : null,
      rows: localRows
    })
    onClose()
  }, [localQuickDate, localRange, localRows, onApply, onClose])

  const handleRestore = useCallback(() => {
    onRestoreDefaults()
    onClose()
  }, [onRestoreDefaults, onClose])

  return (
    <SydModal
      title='Display Settings'
      open={open}
      onClose={onClose}
      size='small'
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', padding: '16px 0' }}>
        <div>
          <div style={sectionHeading}>Date Range</div>
          <SydLabel label='Time Period' helperText='Choose the time period for calculations'>
            <SydSelect
              options={dateOptions}
              value={localQuickDate}
              onChange={handleDateSelect}
            />
          </SydLabel>

          {localQuickDate === 'Custom' ? (
            <div style={{ display: 'flex', gap: '12px', marginTop: '12px' }}>
              <SydLabel label='Start Date' style={{ flex: 1 }}>
                <SydInput
                  type='date'
                  value={localRange?.startDate ?? ''}
                  onChange={handleStartDate}
                />
              </SydLabel>
              <SydLabel label='End Date' style={{ flex: 1 }}>
                <SydInput
                  type='date'
                  value={localRange?.endDate ?? ''}
                  onChange={handleEndDate}
                />
              </SydLabel>
            </div>
          ) : null}
        </div>

        <div>
          <div style={sectionHeading}>Rows</div>
          <DndProvider backend={HTML5Backend}>
            <div style={{ maxHeight: '340px', overflowY: 'auto' }}>
              {(localRows || []).map((row, i) => (
                <SettingsRow
                  key={row.key}
                  row={row}
                  index={i}
                  activityRows={activityRows}
                  onToggle={handleToggle}
                  onRowFieldChange={handleRowFieldChange}
                  onMoveItem={handleMoveItem}
                />
              ))}
            </div>
          </DndProvider>
        </div>
      </div>

      <SydModalActions>
        <SydButton variant='ghost' size='md' onClick={handleRestore}>
          Restore Defaults
        </SydButton>
        <div style={{ flex: 1 }} />
        <SydButton variant='ghost' size='md' onClick={onClose}>
          Cancel
        </SydButton>
        <SydButton variant='primary' size='md' onClick={handleApply}>
          Apply
        </SydButton>
      </SydModalActions>
    </SydModal>
  )
}

ActivitySummarySettingsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  config: PropTypes.object.isRequired,
  quickDate: PropTypes.string.isRequired,
  customDateRange: PropTypes.shape({
    startDate: PropTypes.string,
    endDate: PropTypes.string
  }),
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  onApply: PropTypes.func.isRequired,
  onRestoreDefaults: PropTypes.func.isRequired
}
